import { useState } from 'react';
import { PlugZapIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { EmptyState } from '@/shared/components/EmptyState';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';

import { useHarnesses } from '../hooks/useHarnesses';
import { useHarnessTypes } from '../hooks/useHarnessTypes';
import { useCreateHarness } from '../hooks/useCreateHarness';
import { useDeleteHarness } from '../hooks/useDeleteHarness';
import { useUpdateHarness } from '../hooks/useUpdateHarness';
import type { Harness } from '../types';
import { HarnessDeleteDialog } from './HarnessDeleteDialog';
import { HarnessFormSheet } from './HarnessFormSheet';
import type { HarnessFormMode, HarnessFormValues } from './HarnessFormSheet';
import { HarnessList } from './HarnessList';
import { SkeletonRows } from '@/shared/components/PendingRows';

/**
 * The harnesses section of the admin page (spec 240).
 *
 * Mirrors `llm-profiles/components/LlmProfilesSection.tsx`'s state machine
 * (loading, load-failed with retry, empty, loaded). One list and one
 * create/edit/delete dialog state machine serve every harness type; which types
 * can be created comes from the deployment (`useHarnessTypes`), never from here.
 */
export function HarnessesSection() {
  const { entries, isPending, loadFailed, refetch } = useHarnesses();
  const types = useHarnessTypes();

  // A placeholder only once the wait is worth remarking on (`079` FR-017).
  const showPlaceholder = usePendingDisplay(isPending);

  const [dialog, setDialog] = useState<HarnessFormMode | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Harness | null>(null);

  const create = useCreateHarness();
  const update = useUpdateHarness();
  const remove = useDeleteHarness();

  const saving = create.isPending || update.isPending;
  const saveError = dialog?.kind === 'edit' ? update.error : create.error;

  const all = entries ?? [];

  // Only a type a worker can actually run is offered for a new harness
  // (`implemented`); a type listed but unimplemented, or not listed any more,
  // stays on the harnesses already made from it (spec 240 FR-012).
  const creatableTypes = (types.types ?? []).filter((type) => type.implemented);
  const createDisabledReason = types.isPending
    ? 'Loading the available harness types…'
    : types.loadFailed
      ? 'The harness types could not be loaded.'
      : creatableTypes.length === 0
        ? 'No harness types are available on this deployment.'
        : undefined;

  function openCreate() {
    create.reset();
    update.reset();
    setDialog({ kind: 'create' });
  }

  function openEdit(entry: Harness) {
    create.reset();
    update.reset();
    setDialog({ kind: 'edit', entry });
  }

  function openDelete(entry: Harness) {
    remove.reset();
    setPendingDelete(entry);
  }

  function handleSubmit(input: HarnessFormValues) {
    if (dialog?.kind === 'edit') {
      update.mutate(
        { id: dialog.entry.id, body: input },
        {
          onSuccess: () => {
            setDialog(null);
            toast.success(`Saved ${input.name}.`);
          },
        },
      );
      return;
    }

    create.mutate(input, {
      onSuccess: () => {
        setDialog(null);
        toast.success(`Added ${input.name}.`);
      },
    });
  }

  function handleDelete() {
    if (!pendingDelete) return;
    const { id, name } = pendingDelete;
    remove.mutate(id, {
      onSuccess: () => {
        setPendingDelete(null);
        toast.success(`Deleted ${name}.`);
      },
    });
  }

  if (isPending) {
    if (!showPlaceholder) return null;
    return (
      <div
        aria-busy="true"
        aria-label="Loading Harnesses"
        data-testid="harnesses-region"
      >
        <SkeletonRows label="Loading harnesses" />
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div data-testid="harnesses-region">
        <EmptyState
          icon={PlugZapIcon}
          description="The Harnesses could not be loaded."
          action={
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              Try again
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div
      id="harnesses-section"
      data-testid="harnesses-region"
      className="flex flex-col gap-10"
    >
      <HarnessList
        harnesses={all}
        onEdit={openEdit}
        onDelete={openDelete}
        onCreate={openCreate}
        createDisabledReason={createDisabledReason}
      />

      {types.loadFailed ? (
        <div role="alert" className="flex items-center gap-3 text-sm">
          <span>The harness types could not be loaded.</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void types.refetch()}
          >
            Try again
          </Button>
        </div>
      ) : null}

      {dialog ? (
        <HarnessFormSheet
          key={dialog.kind === 'edit' ? `edit:${dialog.entry.id}` : 'create'}
          mode={dialog}
          types={creatableTypes}
          open
          isPending={saving}
          error={saveError}
          onSubmit={handleSubmit}
          onClearError={dialog.kind === 'edit' ? update.reset : create.reset}
          onCancel={() => {
            setDialog(null);
          }}
        />
      ) : null}

      {pendingDelete ? (
        <HarnessDeleteDialog
          name={pendingDelete.name}
          open
          isPending={remove.isPending}
          error={remove.error}
          onConfirm={handleDelete}
          onCancel={() => {
            setPendingDelete(null);
            remove.reset();
          }}
        />
      ) : null}
    </div>
  );
}
